/**
 * TanStack Query functions for master data API calls
 */

import { buildApiUrl } from '../config/urlConfig.js';
import { queryKeys } from '../queryKeys.js';

/**
 * Fetch master data including booking types, locations, permissions, etc.
 * @returns {Promise<Object>} Master data
 */
export const fetchMasterData = async () => {
	const url = buildApiUrl('user', '/master-data');
	const response = await fetch(url, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			"X-Requested-With": "XMLHttpRequest",
		},
		credentials: "include",
	});

	if (!response.ok) {
		throw new Error(`HTTP ${response.status}: Failed to fetch master data`);
	}

	const data = await response.json();

	if (data.error) {
		throw new Error(data.error);
	}

	return data;
};

/**
 * TanStack Query configuration for fetching master data
 * @returns {Object} Query configuration object
 */
export const useMasterDataQuery = () => {
	return {
		queryKey: queryKeys.masterDataFull(),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error('Query was cancelled');
			}
			return fetchMasterData();
		},
		staleTime: 1000 * 60 * 10, // 10 minutes - master data changes infrequently
		gcTime: 1000 * 60 * 60, // 1 hour - keep in cache longer
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
		// Master data is always needed, so enable by default
		enabled: true,
	};
};